import { inject } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { tapResponse } from "@ngrx/operators";
import { switchMap, tap, pipe, withLatestFrom, filter, map } from "rxjs";
import { PageResult } from "src/app/shared/models/pageResult";
import { RemindersApiService } from "src/app/services/reminders-api.service";
import { Reminder } from "src/app/models/reminder";
import { FilterItemList, SelectedFilter } from "src/app/shared/models/filter.model";
import { toSignal } from "@angular/core/rxjs-interop";
import { ManipulationsToolbarStore } from "src/app/store/manipulations-toolbar-store";
import { GlobalStore } from "src/app/store/global-store";
import { TostesStore } from "src/app/store/tostes-store";
import { TostPositionEnum, TostTypeEnum } from "src/app/shared/models/tostes.model";

export interface ReminderState {
    reminders: PageResult<Reminder>;
    remindersExtra: PageResult<Reminder>;
    // selectedFilter: SelectedFilter;
    filterList: FilterItemList[];
    readonly loading: boolean;
    readonly loadingExtra: boolean;
    isRequestOfExtaraWasEmpty: boolean;

}
export class ReminderStore extends ComponentStore<ReminderState> {
    #reminderService = inject(RemindersApiService);
    #manipulationsToolbarStore = inject(ManipulationsToolbarStore);
    #globalStore = inject(GlobalStore);
    #tostesStore = inject(TostesStore);

    readonly reminders$ = this.select((x) => x.reminders);
    readonly remindersSig$ = toSignal(this.reminders$);
    readonly remindersExtra$ = this.select((x) => x.remindersExtra);
    readonly remindersExtraSig$ = toSignal(this.remindersExtra$);
    readonly totals$ = this.select((x) => x.reminders.totals);
    readonly isEmpty$ = this.select((x) => x.reminders.isEmpty);
    // readonly selectedFilter$ = this.select((x) => x.selectedFilter);
    readonly filterList$ = this.select((x) => x.filterList);
    readonly loadingExtra$ = this.select((x) => x.loadingExtra);
    readonly loadingExtraSig$ = toSignal(this.loadingExtra$);
    readonly loading$ = this.select((x) => x.loading);
    readonly isRequestOfExtaraWasEmptySig$ = toSignal(this.select((x) => x.isRequestOfExtaraWasEmpty))


    constructor() {
        super({
            reminders: { result: [], totals: 0 },
            remindersExtra: { result: [], totals: 0 },
            filterList: [],
            loading: false,
            loadingExtra: false,
            isRequestOfExtaraWasEmpty: false
        });

        this.#manipulationsToolbarStore.selectedFilter$.subscribe(x => this.getReminders());
        this.loadFilterList();
        this.getReminders();

        this.#globalStore.refreshReminder$.subscribe(ev => {
            this.getReminders(true)
        }

        )
    }

    readonly getReminders = this.effect<void | boolean>(
        pipe(
            tap(() => this.patchState({ loading: true })),
            tap((ev) => {
                if (ev) {
                    this.#manipulationsToolbarStore.resetPage()
                }
            }),
            withLatestFrom(this.#manipulationsToolbarStore.selectedFilter$, this.#manipulationsToolbarStore.page$),
            filter(([_, selectedFilter, page]) => selectedFilter != null),
            switchMap(([isRefresh, selectedFilter, page]) => this.#reminderService.getReminders(isRefresh ? 1 : page, selectedFilter!)),
            tap((reminders: PageResult<Reminder>) => this.patchState({ reminders })),
            tap(() => {
                this.patchState({ loading: false }),
                    this.loadRemindersForExtra()
            }),
        )
    );
    readonly loadRemindersForExtra = this.effect<void>(
        pipe(
            tap(() => this.patchState({ loadingExtra: true })),
            tap(() => this.#manipulationsToolbarStore.updatePage()),
            withLatestFrom(this.#manipulationsToolbarStore.selectedFilter$, this.#manipulationsToolbarStore.page$),
            filter(([_, selectedFilter, page]) => selectedFilter != null),
            switchMap(([_, selectedFilter, page]) => this.#reminderService.getRemindersForExtra(page, selectedFilter!)
            ),
            tap((result: PageResult<Reminder>) => {
                this.patchState({
                    remindersExtra: result,
                })
            }),
            tap(() => this.patchState({ loadingExtra: false })),
            // tap((updatedQuestionnairesWithTotals: PageResult<QuestionnaireRow>) => {
            //     if (this.isRequestOfExtaraWasEmptySig$()) { this.updateQuestionnaireWithExtra() }
            //     else {
            //         this.patchState({
            //             questionnairesExtra: updatedQuestionnairesWithTotals,

            //         })
            //     }
            // }),

        )
    );

    readonly updateRemindersWithExtra = this.updater((state) => (
        {
            ...state,
            isRequestOfExtaraWasEmpty: ((this.remindersExtraSig$()?.result == undefined || this.remindersExtraSig$()?.result.length == 0) && this.loadingExtraSig$()) ? true : false,
            reminders: {
                result: [...(this.remindersSig$()?.result || []), ...(this.remindersExtraSig$()?.result || [])],
                totals: this.remindersSig$()?.totals || 0
            },
            remindersExtra: { result: [], totals: 0 }
        }
    ));

    readonly loadFilterList = this.effect<void>(
        pipe(
            switchMap(() => this.#reminderService.getFilterItemList()
            ),

            tap((filterList: FilterItemList[]) => this.patchState({ filterList }))
        )
    );

    readonly deleteReminder = this.effect<number>(
        pipe(
            map(id => {
                const guid = this.#tostesStore.generateGuid();
                this.#tostesStore.addTost({ guid, message: { en: "Delete reminder...", he: "מוחק תזכורת..." }, position: TostPositionEnum.BottomLeft, type: TostTypeEnum.LOAD });
                return { id, guid };
            }),
            switchMap((props) =>
                this.#reminderService.deleteReminderById(props.id).pipe(
                    tapResponse((sucsess) => {
                        this.#manipulationsToolbarStore.resetPage()
                        this.getReminders()
                        this.#tostesStore.removeTostByGuid(props.guid)
                        this.#tostesStore.addTost({ guid: "", message: { en: `The reminder has been successfully deleted`, he: `התזכורת נמחקה בהצלחה ` }, type: TostTypeEnum.SUCCESS, seconds: 5 });
                    },
                        (error) => {
                            this.#tostesStore.removeTostByGuid(props.guid)
                            this.#tostesStore.addTost({ guid: "", message: { en: "An error occurred while deleting the reminder", he: "אירעה שגיאה בעת מחיקת התזכורת" }, type: TostTypeEnum.ERROR, seconds: 5 });
                        })
                )
            )
        )
    );

    // readonly createReminderafsd = this.effect<Reminder>(
    //     pipe(
    //         switchMap((reminder) =>
    //             this.#reminderService.create(reminder).pipe(
    //                 tap((x) => this.#manipulationsToolbarStore.resetPage()),
    //                 tap((x) => this.getReminders()
    //                 )
    //             )
    //         )
    //     )
    // );

    readonly updateReminder = this.effect<Reminder>(
        pipe(
            map(reminder => {
                const guid = this.#tostesStore.generateGuid();
                this.#tostesStore.addTost({ guid, message: { en: "Creating a distribution group...", he: "מעדכן תזכורת..." }, position: TostPositionEnum.BottomLeft, type: TostTypeEnum.LOAD });
                return { reminder, guid };
            }),
            switchMap((props) =>
                this.#reminderService.update(props.reminder).pipe(
                    tapResponse((sucsess) => {
                        this.#manipulationsToolbarStore.resetPage()
                        this.getReminders()
                        this.#tostesStore.removeTostByGuid(props.guid)
                        this.#tostesStore.addTost({ guid: "", message: { en: `The reminder has been successfully updated`, he: `התזכורת עודכנה בהצלחה ` }, type: TostTypeEnum.SUCCESS, seconds: 5 });
                    },
                        (error) => {
                            this.#tostesStore.removeTostByGuid(props.guid)
                            this.#tostesStore.addTost({ guid: "", message: { en: "An error occurred while updating the reminder", he: "אירעה שגיאה בעת עדכון התזכורת" }, type: TostTypeEnum.ERROR, seconds: 5 });
                        }),

                )
            )
        )
    );
}
